/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import * as React from 'react';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import WestIcon from '@mui/icons-material/West';
import useMediaQuery from '@mui/material/useMediaQuery';

import styles from './SideMenu.module.scss';
import ThemeToggler from './ThemeToggler';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { mobileSideBarOpen, openSideBar, selectIsMobileSidebarOpen, selectIsSidebarOpen } from '../ui/uiSlice';
import { selectCreatorMode } from '../chat/chatSlice';
import clsx from 'clsx';

// Update $drawer-width in sizes.scss if you change this
const drawerWidth = 300;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  overflowY: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  overflowY: 'hidden',

  width: `calc(${theme.spacing(10)} + 1px)`,
});

const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: drawerWidth,
  zIndex: 999,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

type SideMenuProps = {
  children: JSX.Element[];
  logo: JSX.Element;
  sideMenu: JSX.Element;
  showThemeToggler?: boolean;
};

export function useIsTemporaryMenu() {
  return useMediaQuery('(max-width: 800px)');
}

export function useIsSidebarOpen() {
  const isTemporary = useIsTemporaryMenu();
  const permOpen = useAppSelector(selectIsSidebarOpen);
  const mobileOpen = useAppSelector(selectIsMobileSidebarOpen);
  return isTemporary ? mobileOpen : permOpen;
}

export function MenuHamburger({
  className,
  style,
  mobileOnly,
}: {
  className?: string;
  mobileOnly?: boolean;
  style?: CSSObject;
}) {
  const dispatch = useAppDispatch();
  const isTemporary = useIsTemporaryMenu();
  const open = useIsSidebarOpen();

  const handleDrawerToggle = () => {
    dispatch(isTemporary ? mobileSideBarOpen(!open) : openSideBar(!open));
  };

  if (mobileOnly && !isTemporary) return null;

  return (
    <IconButton
      onClick={handleDrawerToggle}
      className={className}
      style={{ width: 'fit-content', height: 'fit-content', ...(style as any) }}
      id="hamburgerMenu"
      data-open={open}
      aria-label={open && !mobileOnly ? 'collapse sidemenu' : 'expand sidemenu'}
    >
      {open && !mobileOnly ? <WestIcon className={styles.westIcon} /> : <MenuIcon className={styles.menuIcon} />}
    </IconButton>
  );
}

export default function SideMenu({ children, logo, sideMenu, showThemeToggler }: SideMenuProps) {
  const dispatch = useAppDispatch();
  const isTemporary = useIsTemporaryMenu();
  const open = useIsSidebarOpen();
  const theme = useTheme();
  const borderColor = 'var(--menuborder)';
  const location = useLocation();
  const paperProps = React.useMemo(
    () => ({
      sx: {
        backgroundColor: theme.palette.lightgray.main,
        borderColor,
      },
    }),
    [borderColor, theme.palette.lightgray.main]
  );

  React.useEffect(() => {
    if (isTemporary) {
      dispatch(mobileSideBarOpen(false));
    }
  }, [dispatch, isTemporary]);

  return (
    <Box className={styles.box}>
      {children}
      {/* We need two drawers..  one that is temporary and one is that permanent */}
      <Drawer
        variant={isTemporary ? 'temporary' : 'permanent'}
        open={open}
        onClose={() => dispatch(isTemporary ? mobileSideBarOpen(false) : openSideBar(false))}
        PaperProps={paperProps}
      >
        <SideMenuInner logo={logo} sideMenu={sideMenu} showThemeToggler={showThemeToggler} />
      </Drawer>
      <Box
        component="main"
        data-ishome={location.pathname.includes('home') ? 'true' : 'false'}
        className={clsx(isTemporary ? styles.mobileboxmain : styles.boxmain, open && !isTemporary && styles.open)}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

function SideMenuInner({
  logo,
  sideMenu,
  showThemeToggler,
}: Pick<SideMenuProps, 'logo' | 'sideMenu' | 'showThemeToggler'>) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isCreatorMode = useAppSelector(selectCreatorMode);
  // When you click on the logo, go to /chat page and reset the state
  const handleLogoClick = React.useMemo(() => {
    return () => {
      navigate(!isCreatorMode ? '/home#_' : '/chat#_');
    };
  }, [navigate, isCreatorMode]);
  return (
    <>
      <Toolbar variant="dense" style={{ padding: 0, marginBottom: 61 }}>
        <div className={styles.logoContainer} onClick={handleLogoClick}>
          <div className={styles.logo}>{logo}</div>
        </div>
        <MenuHamburger className={styles.hamburger} />
      </Toolbar>
      {sideMenu}
      {(showThemeToggler || theme.palette.mode === 'dark') && <ThemeToggler className={styles.themeToggler} />}
    </>
  );
}
